import { useMemo, type ReactNode } from "react";
import { useTranslations } from "use-intl";
import clsx from "clsx";
import {
  MapPinIcon,
  ForkKnifeIcon,
  CarIcon,
  BuildingsIcon,
  AirplaneTakeoffIcon,
} from "@phosphor-icons/react";
import type { FindPackageByIdData } from "@/app/lib/types/codegen";
import { CollapsibleCard } from "../../CollapsibleCard";

const iconMap: Record<string, React.ElementType> = {
  MapPin: MapPinIcon,
  ForkKnife: ForkKnifeIcon,
  Car: CarIcon,
  Buildings: BuildingsIcon,
  AirplaneTakeoff: AirplaneTakeoffIcon,
};

type Props = {
  title: ReactNode;
  features: FindPackageByIdData["features"];
  type: FindPackageByIdData["features"][number]["type"];
};

function FeaturesCard({ title, features, type }: Props) {
  const filteredFeatures = useMemo(
    () => features.filter((feature) => feature.type === type),
    [features, type],
  );

  if (filteredFeatures.length === 0) {
    return null;
  }

  return (
    <CollapsibleCard
      title={title}
      collapsible={type === "EXCLUDED"}
      expandedByDefault={type === "INCLUDED"}
    >
      <ul className="space-y-2">
        {filteredFeatures.map((feature) => {
          const IconComponent = iconMap[feature.icon];

          return (
            <li key={feature.id} className="flex items-center gap-2">
              <span
                className={clsx(
                  "flex size-8 shrink-0 items-center justify-center rounded-xl",
                  {
                    "bg-surface-accent-softer text-surface-accent-strong":
                      type === "INCLUDED",
                    "bg-surface-background text-text-tertiary":
                      type === "EXCLUDED",
                  },
                )}
                aria-hidden
              >
                <IconComponent className="size-4" />
              </span>
              <span className="text-sm font-medium text-text-primary">
                {feature.name}
              </span>
            </li>
          );
        })}
      </ul>
    </CollapsibleCard>
  );
}

export function InclusionsCard({ trip }: { trip: FindPackageByIdData }) {
  const t = useTranslations();

  return (
    <FeaturesCard
      title={t("trips.inclusions.title")}
      features={trip.features}
      type="INCLUDED"
    />
  );
}

export function ExclusionsCard({ trip }: { trip: FindPackageByIdData }) {
  const t = useTranslations();

  return (
    <FeaturesCard
      title={t("trips.exclusions.title")}
      features={trip.features}
      type="EXCLUDED"
    />
  );
}
